import { asArrayFirst, toFiniteFirst, toFiniteVector, } from '@cornerstonejs/utils';
import { readOwn } from '../safeFunctions/index.js';
import { planeGeometry } from './seriesFunctions.js';
const SPACING_TOLERANCE = 0.2;
const item = (value) => asArrayFirst(value);
function vectorAt(source, path, field, length) {
    let current = source;
    for (const sequence of path) {
        current = item(readOwn(current, sequence));
    }
    return toFiniteVector(readOwn(current, field), length);
}
function geometryOf(positions, orientation) {
    return planeGeometry(positions.map((ImagePositionPatient, index) => ({
        SOPInstanceUID: String(index),
        ImagePositionPatient,
        ImageOrientationPatient: orientation,
    })), { args: { tolerance: SPACING_TOLERANCE } });
}
const hasPixelSpacing = (instance) => toFiniteVector(readOwn(instance, 'PixelSpacing'), 2) !== undefined;
function isSingleFrameVolume(instances) {
    if (instances.length < 2) {
        return false;
    }
    const [first] = instances;
    const same = (name) => (instance) => toFiniteFirst(readOwn(instance, name)) ===
        toFiniteFirst(readOwn(first, name));
    if (!instances.every(same('Rows')) ||
        !instances.every(same('Columns')) ||
        !instances.every(same('SamplesPerPixel')) ||
        !instances.every((instance) => hasPixelSpacing(instance))) {
        return false;
    }
    const geometry = planeGeometry([...instances], {
        args: { tolerance: SPACING_TOLERANCE },
    });
    return (geometry.irregularCount === 0 &&
        geometry.positions >= 2 &&
        !geometry.duplicates);
}
function framePositions(instance) {
    const perFrame = asArrayFromSequence(readOwn(instance, 'PerFrameFunctionalGroupsSequence')).map((group) => vectorAt(group, ['PlanePositionSequence'], 'ImagePositionPatient', 3));
    if (perFrame.filter(Boolean).length >= 2) {
        return perFrame;
    }
    const shared = vectorAt(instance, ['SharedFunctionalGroupsSequence', 'PlanePositionSequence'], 'ImagePositionPatient', 3);
    const count = toFiniteFirst(instance.NumberOfFrames, perFrame.length);
    return shared ? new Array(count).fill(shared) : [];
}
function asArrayFromSequence(value) {
    return Array.isArray(value) ? value : value == null ? [] : [value];
}
function isMultiFrameVolume(instance) {
    const perFrame0 = item(readOwn(instance, 'PerFrameFunctionalGroupsSequence'));
    const shared = item(readOwn(instance, 'SharedFunctionalGroupsSequence'));
    const detector = item(readOwn(instance, 'DetectorInformationSequence'));
    const hasPixelMeasures = readOwn(perFrame0, 'PixelMeasuresSequence') != null ||
        readOwn(shared, 'PixelMeasuresSequence') != null ||
        (instance.PixelSpacing != null &&
            (instance.SliceThickness != null ||
                instance.SpacingBetweenFrames != null));
    const orientation = toFiniteVector(instance.ImageOrientationPatient, 6) ??
        vectorAt(shared, ['PlaneOrientationSequence'], 'ImageOrientationPatient', 6) ??
        vectorAt(perFrame0, ['PlaneOrientationSequence'], 'ImageOrientationPatient', 6) ??
        toFiniteVector(readOwn(detector, 'ImageOrientationPatient'), 6);
    const hasPosition = readOwn(perFrame0, 'PlanePositionSequence') != null ||
        readOwn(perFrame0, 'CTPositionSequence') != null ||
        instance.ImagePositionPatient != null ||
        readOwn(detector, 'ImagePositionPatient') != null;
    if (!hasPixelMeasures || !orientation || !hasPosition) {
        return false;
    }
    if (String(instance.Modality ?? '').includes('NM')) {
        const subType = asArrayFromSequence(instance.ImageType)[2];
        if (subType !== 'RECON TOMO' && subType !== 'RECON GATED TOMO') {
            return false;
        }
    }
    const positions = framePositions(instance);
    if (positions.filter(Boolean).length < 2) {
        return true;
    }
    return geometryOf(positions, orientation).positions >= 2;
}
export function isReconstructableInstances(instances) {
    const defined = instances.filter(Boolean);
    if (!defined.length) {
        return false;
    }
    return toFiniteFirst(defined[0].NumberOfFrames, 0) > 1
        ? isMultiFrameVolume(defined[0])
        : isSingleFrameVolume(defined);
}
